// Lib
import React, { useState } from 'react';
import { isEmpty } from 'lodash/fp';

// Hooks
import { useAssignmentSuggestions, type AssignmentSuggestion } from '../hooks/useAssignmentSuggestions';
import useListFocusManager from '../../../components/lists/useListFocusManager';
import useListKeyboardManager, { ListDirection } from '../../../components/lists/useListKeyboardManager';

// Components
import AssignmentSearchForm from '../../task/pills/assignment/AssignmentSearchForm';
import AssignmentSuggestionResults from '../../task/pills/assignment/AssignmentSuggestionResults';

// Types
import type { TaskItemAssignment } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';

// Styles - shared with the element assignment popup, since both render the same layout.
import '../../task/pills/assignment/TaskAssignmentPopupContent.scss';

type TaskItemAssignmentPopupContentProps = {
    /** The owning task node's `assignments` attr. */
    assignments: TaskItemAssignment[];
    /** Adds an assignment to the owning node. The popup closes itself after. */
    onAssign: (suggestion: AssignmentSuggestion) => void;
    /** Clears all assignees (backspace/delete with an empty search). The popup closes itself after. */
    onRemoveAll: () => void;
    /** Supplied by the chip selection popover/sheet render args. */
    closePopup: () => void;
};

/**
 * Node-attr backed assignee picker content for a task item chip. Mirrors the
 * element `TaskAssignmentPopupContent`, but is driven by the owning node's
 * attrs via props, sources suggestions through `useAssignmentSuggestions`, and
 * leaves focus/dismissal to the chip selection popover/sheet (no
 * `useTaskPopupFocus` element-editor mechanics).
 */
const TaskItemAssignmentPopupContent: React.FC<TaskItemAssignmentPopupContentProps> = ({
    assignments,
    onAssign,
    onRemoveAll,
    closePopup,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // For now, we only support a single assignee
    const currentlyAssignedUserId = assignments[0]?.assigneeId ?? null;

    const { enabledSuggestions, disabledSuggestions } = useAssignmentSuggestions({
        searchTerm,
        currentlyAssignedUserId,
    });

    const onClickSuggestion = (suggestion: AssignmentSuggestion | undefined) => {
        if (!suggestion) return;

        onAssign(suggestion);
        closePopup();
    };

    const { focusedIndex, moveListFocusDown, moveListFocusUp } = useListFocusManager({
        loop: false,
        initialFocusIndex: -1,
        listSize: enabledSuggestions.length,
    });

    useListKeyboardManager({
        listDirection: ListDirection.VERTICAL,
        moveListFocusDown,
        moveListFocusUp,
        onSubmit: () => onClickSuggestion(enabledSuggestions[focusedIndex]),
        onClose: () => closePopup(),
        onDelete: (event) => {
            // If the search term isn't empty, let the browser handle the backspace / delete
            if (!isEmpty(searchTerm)) return;

            event.preventDefault();
            event.stopPropagation();

            onRemoveAll();
            closePopup();
        },
    });

    return (
        <div className="TaskAssignmentPopupContent">
            <div className="search-section">
                <AssignmentSearchForm searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>
            <AssignmentSuggestionResults
                currentlyAssignedUserId={currentlyAssignedUserId}
                searchTerm={searchTerm}
                focusedIndex={focusedIndex}
                enabledSuggestions={enabledSuggestions}
                disabledSuggestions={disabledSuggestions}
                onClickSuggestion={onClickSuggestion}
            />
        </div>
    );
};

export default TaskItemAssignmentPopupContent;
